import React, { useState } from "react";
import axios from "axios";
import { MyDomain } from "../../App";

export default function BarberSubsMPManageBlock({ activeSub, onRecheck }) {
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const [cancelledSub, setCancelledSub] = useState(null);
  const [error, setError] = useState(null);

  const handleLogout = () => {
    localStorage.removeItem("barber");
    window.location.reload();
  };

  // Format "DD-MM-YYYY" → "April 6, 2026"
  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return "—";
    const [day, month, year] = dateStr.split("-").map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getBarber = () => {
    const barberRaw = localStorage.getItem("barber");
    return barberRaw ? JSON.parse(barberRaw) : null;
  };

  // ── CANCEL
  const handleCancel = async () => {
    setError(null);
    setIsCancelling(true);

    try {
      const barber = getBarber();
      if (!barber?.phoneNumber) {
        setError("Session expired. Please log in again.");
        setIsCancelling(false);
        return;
      }

      const response = await axios.post(
        `${MyDomain}Subscription/CancelSubscription`,
        { phoneNumber: barber.phoneNumber },
      );

      setCancelledSub(response.data);
      setIsCancelling(false);
      setCancelled(true);
    } catch (err) {
      setIsCancelling(false);
      setError(
        err?.response?.data?.message ||
          err?.response?.data ||
          "Failed to cancel. Please try again.",
      );
    }
  };

  // ── CANCELLED FULL STATE ───────────────────────────────────────────────────
  if (cancelled && cancelledSub) {
    return (
      <div style={styles.page}>
        <header style={styles.header}>
          <div style={styles.logo}>
            <span style={styles.logoIcon}>✦</span>
            <span style={styles.logoText}>BARBR</span>
          </div>
          <button style={styles.logoutBtn} onClick={handleLogout}>
            Sign out
          </button>
        </header>

        <main style={styles.main}>
          <div style={styles.cancelledWrap}>
            <div style={styles.cancelledIconCircle}>✕</div>
            <p style={styles.cancelledEyebrow}>SUBSCRIPTION CANCELLED</p>
            <h2 style={styles.cancelledTitle}>We're sorry to see you go</h2>
            <p style={styles.cancelledSubtitle}>
              Your cancellation has been confirmed.
            </p>

            <div style={styles.cancelInfoCard}>
              <div style={styles.cancelInfoRow}>
                <span style={styles.cancelInfoLabel}>PLAN</span>
                <span style={styles.cancelInfoValue}>Pro Plan</span>
              </div>
              <div style={styles.cancelInfoDivider} />
              <div style={styles.cancelInfoRow}>
                <span style={styles.cancelInfoLabel}>STATUS</span>
                <span style={styles.cancelStatusBadge}>Cancelled</span>
              </div>
              <div style={styles.cancelInfoDivider} />
              <div style={styles.cancelInfoRow}>
                <span style={styles.cancelInfoLabel}>STARTED ON</span>
                <span style={styles.cancelInfoValue}>
                  {formatDisplayDate(cancelledSub.dateStart)}
                </span>
              </div>
              <div style={styles.cancelInfoDivider} />
              <div style={styles.cancelInfoRow}>
                <span style={styles.cancelInfoLabel}>ACCESS ENDS ON</span>
                <span style={{ ...styles.cancelInfoValue, color: "#c8a96e" }}>
                  {formatDisplayDate(cancelledSub.dateEnd)}
                </span>
              </div>
              <div style={styles.cancelInfoDivider} />
              <div style={styles.cancelInfoRow}>
                <span style={styles.cancelInfoLabel}>AMOUNT PAID</span>
                <span style={styles.cancelInfoValue}>
                  ${cancelledSub.price}/mo
                </span>
              </div>
            </div>

            <div style={styles.noticeBanner}>
              <span style={styles.noticeIcon}>ℹ</span>
              <p style={styles.noticeText}>
                Since you already paid for this period, you'll keep full access
                to all Pro features until{" "}
                <strong style={{ color: "#c8a96e" }}>
                  {formatDisplayDate(cancelledSub.dateEnd)}
                </strong>
                . No further charges will be made.
              </p>
            </div>

            <button
              style={styles.resubBtn}
              onClick={() => {
                setCancelled(false);
                setCancelledSub(null);
                if (onRecheck) onRecheck();
              }}
            >
              Resubscribe
            </button>
          </div>
        </main>

        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;1,400&family=DM+Mono:wght@300;400&display=swap');
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body { background: #0f0d0a; }
        `}</style>
      </div>
    );
  }

  // ── ACTIVE SUBSCRIPTION VIEW ───────────────────────────────────────────────
  return (
    <div style={styles.page}>
      <header style={styles.header}>
        <div style={styles.logo}>
          <span style={styles.logoIcon}>✦</span>
          <span style={styles.logoText}>BARBR</span>
        </div>
        <button style={styles.logoutBtn} onClick={handleLogout}>
          Sign out
        </button>
      </header>

      <main style={styles.main}>
        <div style={styles.topText}>
          <p style={styles.eyebrow}>SUBSCRIPTION</p>
          <h1 style={styles.headline}>
            Your <em style={styles.em}>Plan</em>
          </h1>
          <p style={styles.subline}>Manage your active subscription below.</p>
        </div>

        {/* Status Card */}
        <div style={styles.statusCard}>
          <div style={styles.statusLeft}>
            <div style={styles.activePill}>
              <span style={styles.activeDot} />
              Active
            </div>
            <h2 style={styles.planTitle}>Pro Plan</h2>
            <p style={styles.planPrice}>
              <span style={styles.priceDollar}>$</span>
              <span style={styles.priceAmount}>{activeSub?.price ?? "17"}</span>
              <span style={styles.pricePeriod}>/mo</span>
            </p>
          </div>
          <div style={styles.divider} />
          <div style={styles.statusRight}>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>NEXT BILLING</span>
              <span style={styles.infoValue}>
                {formatDisplayDate(activeSub?.dateEnd)}
              </span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>MEMBER SINCE</span>
              <span style={styles.infoValue}>
                {formatDisplayDate(activeSub?.dateStart)}
              </span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>PHONE</span>
              <span style={styles.infoValue}>
                {activeSub?.countryNumber} {activeSub?.barberPhoneNumber}
              </span>
            </div>
          </div>
        </div>

        {/* Features */}
        <div style={styles.featuresCard}>
          <p style={styles.featuresTitle}>What's included in your plan</p>
          <div style={styles.featuresGrid}>
            {[
              "Unlimited clients",
              "Advanced analytics",
              "Support 24/7",
              "Custom branding",
            ].map((f, i) => (
              <div key={i} style={styles.featureItem}>
                <span style={styles.featureCheck}>✓</span>
                {f}
              </div>
            ))}
          </div>
        </div>

        {/* Error */}
        {error && <div style={styles.errorBox}>⚠ {error}</div>}

        {/* Cancel trigger button */}
        {!showCancelConfirm && (
          <button
            style={styles.cancelTriggerBtn}
            onClick={() => setShowCancelConfirm(true)}
          >
            Cancel Subscription
          </button>
        )}

        {/* Cancel confirm box */}
        {showCancelConfirm && (
          <div style={styles.confirmBox}>
            <p style={styles.confirmTitle}>Are you sure?</p>
            <p style={styles.confirmText}>
              You'll lose access to all Pro features on{" "}
              <span style={{ color: "#c8a96e" }}>
                {formatDisplayDate(activeSub?.dateEnd)}
              </span>
              . Since you've already paid, you'll keep access until that date.
            </p>
            <div style={styles.confirmBtns}>
              <button
                style={styles.confirmKeepBtn}
                onClick={() => setShowCancelConfirm(false)}
              >
                Keep Plan
              </button>
              <button
                style={{
                  ...styles.confirmCancelBtn,
                  ...(isCancelling ? styles.btnDisabled : {}),
                }}
                onClick={handleCancel}
                disabled={isCancelling}
              >
                {isCancelling ? "Cancelling…" : "Yes, Cancel"}
              </button>
            </div>
          </div>
        )}
      </main>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;1,400&family=DM+Mono:wght@300;400&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: #0f0d0a; }
      `}</style>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: "#0f0d0a",
    color: "#e8ddd0",
    fontFamily: "'DM Mono', monospace",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "20px 40px",
    borderBottom: "1px solid #2a2520",
  },
  logo: { display: "flex", alignItems: "center", gap: "10px" },
  logoIcon: { color: "#c8a96e", fontSize: "18px" },
  logoText: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: "22px",
    fontWeight: 600,
    letterSpacing: "0.3em",
    color: "#e8ddd0",
  },
  logoutBtn: {
    background: "transparent",
    border: "1px solid #3a3028",
    color: "#8a7a6a",
    padding: "8px 18px",
    borderRadius: "4px",
    cursor: "pointer",
    fontFamily: "'DM Mono', monospace",
    fontSize: "12px",
    letterSpacing: "0.05em",
  },
  main: { maxWidth: "700px", margin: "0 auto", padding: "60px 30px" },
  topText: { textAlign: "center", marginBottom: "48px" },
  eyebrow: {
    fontSize: "11px",
    letterSpacing: "0.3em",
    color: "#c8a96e",
    marginBottom: "12px",
  },
  headline: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: "52px",
    fontWeight: 400,
    lineHeight: 1.1,
    marginBottom: "14px",
    color: "#f0e8dc",
  },
  em: { fontStyle: "italic", color: "#c8a96e" },
  subline: { color: "#7a6a5a", fontSize: "14px", letterSpacing: "0.03em" },

  // Status card
  statusCard: {
    background: "#17140f",
    border: "1px solid #2a2520",
    borderRadius: "16px",
    padding: "32px",
    display: "flex",
    gap: "32px",
    alignItems: "center",
    marginBottom: "20px",
    flexWrap: "wrap",
  },
  statusLeft: { flex: 1, minWidth: "160px" },
  activePill: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    background: "rgba(200,169,110,0.1)",
    border: "1px solid rgba(200,169,110,0.3)",
    borderRadius: "20px",
    padding: "4px 12px",
    fontSize: "10px",
    letterSpacing: "0.15em",
    color: "#c8a96e",
    marginBottom: "14px",
  },
  activeDot: {
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    background: "#c8a96e",
  },
  planTitle: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: "28px",
    fontWeight: 600,
    color: "#e8ddd0",
    marginBottom: "6px",
  },
  planPrice: { display: "flex", alignItems: "baseline", gap: "2px" },
  priceDollar: { fontSize: "16px", color: "#7a6a5a" },
  priceAmount: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: "42px",
    fontWeight: 600,
    color: "#c8a96e",
    lineHeight: 1,
  },
  pricePeriod: { fontSize: "13px", color: "#5a5048" },
  divider: {
    width: "1px",
    height: "100px",
    background: "#2a2520",
    flexShrink: 0,
  },
  statusRight: {
    flex: 2,
    minWidth: "200px",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  infoRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  infoLabel: { fontSize: "9px", letterSpacing: "0.2em", color: "#5a5048" },
  infoValue: { fontSize: "13px", color: "#e8ddd0" },

  // Features
  featuresCard: {
    background: "#17140f",
    border: "1px solid #2a2520",
    borderRadius: "12px",
    padding: "24px 28px",
    marginBottom: "32px",
  },
  featuresTitle: {
    fontSize: "10px",
    letterSpacing: "0.2em",
    color: "#5a5048",
    marginBottom: "16px",
  },
  featuresGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "10px",
  },
  featureItem: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "12px",
    color: "#8a7a6a",
  },
  featureCheck: { color: "#c8a96e", fontSize: "11px", fontWeight: 700 },

  errorBox: {
    background: "rgba(180,60,40,0.1)",
    border: "1px solid rgba(180,60,40,0.3)",
    borderRadius: "8px",
    padding: "12px 16px",
    marginBottom: "16px",
    fontSize: "12px",
    color: "#d07060",
    letterSpacing: "0.03em",
  },

  cancelTriggerBtn: {
    background: "transparent",
    border: "1px solid #3a3028",
    color: "#6a5a4a",
    padding: "10px 20px",
    borderRadius: "8px",
    cursor: "pointer",
    fontFamily: "'DM Mono', monospace",
    fontSize: "12px",
    letterSpacing: "0.08em",
    marginTop: "4px",
  },

  // Confirm box
  confirmBox: {
    background: "#17140f",
    border: "1px solid #3a2a20",
    borderRadius: "12px",
    padding: "28px",
    textAlign: "center",
  },
  confirmTitle: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: "24px",
    color: "#e8ddd0",
    marginBottom: "10px",
  },
  confirmText: {
    fontSize: "13px",
    color: "#7a6a5a",
    marginBottom: "24px",
    lineHeight: 1.7,
  },
  confirmBtns: { display: "flex", gap: "12px", justifyContent: "center" },
  confirmKeepBtn: {
    padding: "12px 28px",
    background: "linear-gradient(135deg, #c8a96e, #a08040)",
    border: "none",
    borderRadius: "8px",
    color: "#0f0d0a",
    cursor: "pointer",
    fontFamily: "'DM Mono', monospace",
    fontSize: "13px",
    fontWeight: 600,
    letterSpacing: "0.06em",
  },
  confirmCancelBtn: {
    padding: "12px 28px",
    background: "transparent",
    border: "1px solid #5a3a2a",
    borderRadius: "8px",
    color: "#8a5a4a",
    cursor: "pointer",
    fontFamily: "'DM Mono', monospace",
    fontSize: "13px",
    letterSpacing: "0.06em",
  },
  btnDisabled: { opacity: 0.5, cursor: "not-allowed" },

  // Cancelled state
  cancelledWrap: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    gap: "18px",
  },
  cancelledIconCircle: {
    width: "72px",
    height: "72px",
    borderRadius: "50%",
    border: "2px solid #3a3028",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "26px",
    color: "#6a5a4a",
    marginBottom: "4px",
  },
  cancelledEyebrow: {
    fontSize: "10px",
    letterSpacing: "0.3em",
    color: "#6a5a4a",
  },
  cancelledTitle: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: "38px",
    color: "#e8ddd0",
    fontWeight: 400,
    lineHeight: 1.1,
  },
  cancelledSubtitle: {
    fontSize: "13px",
    color: "#5a5048",
    marginBottom: "8px",
  },
  cancelInfoCard: {
    width: "100%",
    maxWidth: "480px",
    background: "#17140f",
    border: "1px solid #2a2520",
    borderRadius: "14px",
    padding: "24px 28px",
    display: "flex",
    flexDirection: "column",
  },
  cancelInfoRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px 0",
  },
  cancelInfoDivider: { height: "1px", background: "#1e1a14" },
  cancelInfoLabel: {
    fontSize: "9px",
    letterSpacing: "0.2em",
    color: "#5a5048",
  },
  cancelInfoValue: { fontSize: "13px", color: "#e8ddd0" },
  cancelStatusBadge: {
    fontSize: "10px",
    letterSpacing: "0.1em",
    color: "#8a5a4a",
    background: "rgba(138,90,74,0.1)",
    border: "1px solid rgba(138,90,74,0.3)",
    borderRadius: "20px",
    padding: "3px 12px",
  },
  noticeBanner: {
    width: "100%",
    maxWidth: "480px",
    background: "rgba(200,169,110,0.06)",
    border: "1px solid rgba(200,169,110,0.2)",
    borderRadius: "10px",
    padding: "16px 20px",
    display: "flex",
    gap: "12px",
    alignItems: "flex-start",
    textAlign: "left",
  },
  noticeIcon: {
    color: "#c8a96e",
    fontSize: "16px",
    flexShrink: 0,
    marginTop: "1px",
  },
  noticeText: {
    fontSize: "12px",
    color: "#7a6a5a",
    lineHeight: 1.7,
    letterSpacing: "0.02em",
  },
  resubBtn: {
    marginTop: "6px",
    padding: "14px 40px",
    background: "linear-gradient(135deg, #c8a96e, #a08040)",
    border: "none",
    borderRadius: "8px",
    color: "#0f0d0a",
    cursor: "pointer",
    fontFamily: "'DM Mono', monospace",
    fontSize: "13px",
    fontWeight: 600,
    letterSpacing: "0.08em",
  },
};
